/**
 * Users API
 *
 * API endpoints for user management operations
 */

import toast from "react-hot-toast";

import { nextCargoApi } from "../../baseApi";
import { saveUserArgsSchema } from "./schemas";
import type { SaveUser, User } from "./users.types";

/**
 * Users API endpoints
 * Provides CRUD operations for user management
 */
export const usersApi = nextCargoApi.injectEndpoints({
  endpoints: (build) => ({
    /**
     * Get all users
     * @returns Array of users with their details
     */
    getUsers: build.query<User[], void, User[]>({
      query: () => "/users",
      transformResponse: (response) =>
        response.map((user) => ({
          ...user,
          receivesEmail: user.receivesEmail ?? false
        }))
    }),

    /**
     * Get a single user by ID
     * @param id - User ID
     * @returns Full user details
     */
    getUser: build.query<User, Pick<User, "id">>({
      query: ({ id }) => `/users/${id}`,
      keepUnusedDataFor: 0
    }),

    /**
     * Create a new user
     * @param payload - User data with id set to null
     * @returns void
     */
    saveUser: build.mutation<SaveUser["result"], SaveUser["args"]>({
      query: (body) => ({
        url: "/users/save",
        method: "POST",
        body,
        responseHandler: "text"
      }),
      skipSchemaValidation: true,
      //@ts-expect-error zod schema is not compatible with standard schema
      argSchema: saveUserArgsSchema,
      onQueryStarted: async (args, { queryFulfilled, dispatch }) => {
        toast.promise(queryFulfilled, {
          loading: "Salvando usuário...",
          success: "Usuário salvo com sucesso!",
          error: "Erro ao salvar usuário"
        });

        try {
          await queryFulfilled;
          // if (args.name) dispatch(authSlice.actions.setName({ user: args.name }));
        } catch {
          // Error already handled by toast
        }
      }
    }),

    /**
     * Delete a user
     * @param id - User ID to delete
     * @returns void
     */
    deleteUser: build.mutation<void, Pick<User, "id">>({
      query: ({ id }) => ({
        url: `/users/delete/${id}`,
        method: "DELETE"
      })
    })
  })
});

/**
 * Enhance endpoints with cache tag configuration
 */
usersApi.enhanceEndpoints({
  addTagTypes: ["Users"],
  endpoints: {
    getUsers: {
      providesTags: (result) => (result ? [...result.map(({ id }) => ({ type: "Users" as const, id })), { type: "Users", id: "LIST" }] : [{ type: "Users", id: "LIST" }]),
      onQueryStarted: (args, { queryFulfilled }) => {
        toast.promise(queryFulfilled, {
          loading: "Carregando usuários...",
          success: "Usuários carregados com sucesso!",
          error: "Erro ao carregar usuários."
        });
      }
    },
    getUser: {
      providesTags: (result, error, { id }) => [{ type: "Users", id }],
      onQueryStarted: (args, { queryFulfilled }) => {
        toast.promise(queryFulfilled, {
          loading: "Carregando usuário...",
          success: "Usuário carregado com sucesso!",
          error: "Erro ao carregar usuário."
        });
      }
    },
    saveUser: {
      invalidatesTags: (result, error) => (!error && result ? [{ type: "Users", id: result.id! }] : [])
    },
    deleteUser: {
      invalidatesTags: (result, error, { id }) =>
        !error
          ? [
              { type: "Users", id },
              { type: "Users", id: "LIST" }
            ]
          : [],
      onQueryStarted: (args, { queryFulfilled }) => {
        toast.promise(queryFulfilled, {
          loading: "Excluindo usuário...",
          success: "Usuário excluído com sucesso!",
          error: "Erro ao excluir usuário"
        });
      }
    }
  }
});
